/**
 * Organization Hooks - React Query hooks for organization operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationService } from '../services';
import type { UpdateOrganizationRequest } from '../types';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Query keys
export const organizationKeys = {
  all: ['organization'] as const,
  detail: () => [...organizationKeys.all, 'detail'] as const,
};

/**
 * Get current organization
 */
export const useOrganization = () => {
  return useQuery({
    queryKey: organizationKeys.detail(),
    queryFn: () => organizationService.getOrganization(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Update organization
 */
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOrganizationRequest) => organizationService.updateOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail() });
      toast.success('Organization updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update organization'));
    },
  });
};

/**
 * Upload organization logo
 */
export const useUploadLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => organizationService.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail() });
      toast.success('Logo uploaded successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to upload logo'));
    },
  });
};
